import { useState } from "react";
import { X, Coffee, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

/**
 * CoffeeChatDialog — request a Coffee Chat.
 *
 * A Coffee Chat is a private message conversation, not a scheduled call.
 * The requester writes a short introduction; if the other person accepts,
 * a private thread opens automatically. No appointment time is needed.
 */
export function CoffeeChatDialog({ otherUserId, jobId, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/coffee-chats", {
        other_user_id: otherUserId,
        job_id: jobId,
        message,
      });
      toast.success("Coffee chat request sent");
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl border"
        data-testid="coffee-chat-dialog"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl flex items-center gap-2">
            <Coffee size={20} /> Request a Coffee Chat
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-muted"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="mt-3 rounded-xl border border-[hsl(var(--sb-forest))]/25 bg-[hsl(var(--sb-forest))]/5 px-3.5 py-3"
          data-testid="coffee-chat-explanation"
        >
          <p className="text-xs leading-relaxed text-foreground/85 flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0 text-[hsl(var(--sb-forest-2))]" />
            <span>
              A Coffee Chat is a private message conversation that lets authors and beta readers
              discuss a project before committing to work together. Use it to ask questions,
              clarify expectations, discuss availability, and see whether the project feels like a
              good fit. It is not a scheduled video or phone call.
            </span>
          </p>
        </div>

        <div className="mt-5 space-y-2">
          <label className="text-sm font-medium block" htmlFor="coffee-chat-message">
            Your message
          </label>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Send a brief message introducing yourself and explaining what you would like to
            discuss. No appointment time is needed. If the other person accepts, a private chat
            will open.
          </p>
          <textarea
            id="coffee-chat-message"
            rows={5}
            required
            minLength={10}
            maxLength={1000}
            className="sb-input"
            placeholder="Hi! I'd love to chat about your project — a few things I wanted to ask…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid={TID.coffeeChats.inviteMessage}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost border">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || message.trim().length < 10}
            className="btn-primary"
            data-testid={TID.coffeeChats.inviteSubmit}
          >
            {saving ? "Sending…" : "Send request"}
          </button>
        </div>
      </form>
    </div>
  );
}
